import { Sphere } from '@react-three/drei'
import { ThreeEvent } from '@react-three/fiber'
import { formatForLabel } from 'features/siteMap/communalMapStyling/FormatForLabel'
import { useTilesetMapFeatureHelpers } from 'features/siteMap/tilesetMap/features/useTilesetMapFeatureHelpers'
import { TilesetMapFeatureLabel } from 'features/siteMap/tilesetMap/objects/TilesetMapFeatureLabel'
import {
  MapFeaturePattern,
  TilesetMapFeatureStandardMaterial,
} from 'features/siteMapViz'
import React, { ReactNode, useState } from 'react'
import { Vector3 } from 'three'
import { SiteFeaturePrimitiveSphere } from 'types'

export const TilesetMapFeatureSphere3D: React.FC<{
  sphere: SiteFeaturePrimitiveSphere
  name: string | undefined
  color: string
  visible: boolean
  pattern: MapFeaturePattern
  onClick: (e: ThreeEvent<MouseEvent>) => void
  extraLabelDisplay?: ReactNode
}> = ({
  sphere,
  name,
  color,
  visible,
  pattern,
  extraLabelDisplay,
  onClick,
}) => {
  const [showName, setShowName] = useState(false)
  const { convertCartographicToPosition, getFeatureOpacity } =
    useTilesetMapFeatureHelpers()

  const xyz = React.useMemo(
    () => convertCartographicToPosition(sphere.coordinate),
    [convertCartographicToPosition, sphere.coordinate]
  )
  const opacity = getFeatureOpacity(sphere)

  return (
    <group visible={visible}>
      <group position={xyz}>
        <Sphere
          name={sphere.id}
          userData={{ siteFeature: sphere, uid: sphere.id }}
          onPointerMove={(e) => {
            setShowName(
              e.intersections.at(0)?.eventObject.userData.uid === sphere.id
            )
          }}
          onPointerLeave={() => setShowName(false)}
          args={[
            sphere.geometry.radius,
            undefined,
            undefined,
            sphere.geometry.phi_start === null
              ? undefined
              : sphere.geometry.phi_start,
            sphere.geometry.phi_end === null
              ? undefined
              : sphere.geometry.phi_end,
            sphere.geometry.theta_start === null
              ? undefined
              : sphere.geometry.theta_start,
            sphere.geometry.theta_end === null
              ? undefined
              : sphere.geometry.theta_end,
          ]}
          onClick={onClick}>
          <TilesetMapFeatureStandardMaterial
            color={color}
            baseOpacity={opacity}
            pattern={pattern}
            importantFaceLength={sphere.geometry.radius * 2}
          />
        </Sphere>
        {!name ? null : (
          <TilesetMapFeatureLabel
            body={formatForLabel(name, extraLabelDisplay)}
            hovered={showName}
            position={new Vector3(0, sphere.geometry.radius, 0)}
            visible={visible}
          />
        )}
      </group>
    </group>
  )
}
